import { eventIterator, oc } from "@orpc/contract";
import { z } from "zod";
import {
  PricelistEventAction,
  PricelistVersionSource,
} from "@erp/shared-types";

/**
 * A percentage as a decimal string, optionally negative, up to 4 decimal places
 * — matches the DECIMAL(10,4) column and avoids float drift in transit.
 */
const PercentStringSchema = z
  .string()
  .regex(/^-?\d{1,3}(\.\d{1,4})?$/, "Enter a percentage, e.g. 3.5 or -1.25");

export const PricelistTableSchema = z.object({
  headers: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.string())),
});

/**
 * Step 1 of the presigned upload flow: reserve a staging key and a URL the
 * browser PUTs the raw bytes to. The file's kind is not knowable yet — the
 * server has no bytes — so the size gate here is the most generous cap of any
 * kind; the per-kind cap is enforced by `uploadPricelist` once the staged
 * blob's header bytes say what it is.
 */
export const GetPricelistUploadUrlInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  /** The browser-reported MIME type; used only as the PUT's Content-Type. */
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().positive(),
});

export const GetPricelistUploadUrlResponseSchema = z.object({
  /** Opaque staging key, handed back verbatim to `uploadPricelist`. */
  uploadKey: z.string(),
  uploadUrl: z.string().url(),
});

export const UploadPricelistInputSchema = z.object({
  /** The staging key `getUploadUrl` answered with, its blob now uploaded. */
  uploadKey: z.string().min(1),
  fileName: z.string(),
  companyId: z.uuid().optional(),
});

/**
 * What an upload can promise once extraction no longer happens on the request.
 *
 * There is deliberately no `csvDocumentId` here any more: the CSV does not exist
 * yet when this answers. Callers that need it read it from the pricelist itself
 * (`get` returns `csvDocuments`), and `backgroundJobId` is how they find out when
 * it is worth looking.
 */
export const UploadPricelistResponseSchema = z.object({
  pdfDocumentId: z.uuid(),
  /** The `core_background_jobs` row tracking the extraction. */
  backgroundJobId: z.uuid(),
});

/** The largest pricelist (total rows) that `getExtractedCsv` returns whole. */
export const EXTRACTED_CSV_FULL_READ_MAX_ROWS = 20_000;

/** Row-window size limits for paginated `getExtractedCsv` reads. */
export const EXTRACTED_CSV_WINDOW_MAX_ROWS = 1_000;

export const GetExtractedCsvInputSchema = z.object({
  documentId: z.uuid(),
  /**
   * A row window of one table, for pricelists too large to return whole. When
   * present, only the addressed table carries rows — every table still reports
   * its headers and row count. When absent, the response is complete only if
   * the pricelist is within EXTRACTED_CSV_FULL_READ_MAX_ROWS total rows;
   * otherwise all row arrays are empty and callers page through windows.
   */
  window: z
    .object({
      /** Index into the response's `tables` array. */
      table: z.number().int().nonnegative(),
      offset: z.number().int().nonnegative(),
      limit: z.number().int().positive().max(EXTRACTED_CSV_WINDOW_MAX_ROWS),
    })
    .optional(),
});

export const ExtractedCsvTableSchema = z.object({
  headers: z.array(z.string()),
  /** Total rows in the table, regardless of how many `rows` carries. */
  rowCount: z.number().int().nonnegative(),
  rows: z.array(z.record(z.string(), z.string())),
});

export const GetExtractedCsvResponseSchema = z.object({
  tables: z.array(ExtractedCsvTableSchema),
  /**
   * True when every table's `rows` holds all of its rows — the editing
   * surfaces are only safe to enable then, since saves post whole tables.
   */
  complete: z.boolean(),
});

/**
 * Matching rows one search returns before it stops looking. A cap is what makes
 * the search cheap: it lets the server abandon the rest of the blob, which for a
 * common term is most of it. The cost is that totals past the cap are unknown,
 * reported as `truncated` and rendered "200+".
 */
export const EXTRACTED_CSV_SEARCH_MAX_HITS = 200;

/** Shortest query worth scanning a whole pricelist for. */
export const EXTRACTED_CSV_SEARCH_MIN_QUERY = 2;

export const SearchExtractedCsvInputSchema = z.object({
  /** The extracted-CSV document, the same id `getExtractedCsv` takes. */
  documentId: z.uuid(),
  /** Literal text, matched case-insensitively against each cell. */
  query: z.string().min(EXTRACTED_CSV_SEARCH_MIN_QUERY).max(200),
  maxHits: z
    .number()
    .int()
    .positive()
    .max(EXTRACTED_CSV_SEARCH_MAX_HITS)
    .optional(),
});

export const PricelistSearchHitSchema = z.object({
  /**
   * Index into the `tables` array of the same pricelist's `getExtractedCsv`
   * response — tables are ordered by first appearance in both.
   */
  table: z.number().int().nonnegative(),
  /** The row's index within its table, so a window offset can address it. */
  rowIndex: z.number().int().nonnegative(),
  row: z.record(z.string(), z.string()),
});

/**
 * Hits arrive as they are found rather than in one response, so the first match
 * of a cold search reaches the screen long before the scan finishes.
 */
export const PricelistSearchEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("hit"),
    hit: PricelistSearchHitSchema,
  }),
  z.object({
    type: z.literal("done"),
    hitCount: z.number().int().nonnegative(),
    /** True when the hit cap stopped the scan, so there may be more matches. */
    truncated: z.boolean(),
    /** Rows examined, wall-clock ms, and whether the bytes were already resident. */
    rowsScanned: z.number().int().nonnegative(),
    elapsedMs: z.number().nonnegative(),
    cached: z.boolean(),
  }),
]);

export const GetPricelistDocumentUrlInputSchema = z.object({
  documentId: z.uuid(),
});

export const GetPricelistDocumentUrlResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
});

export const ListUnassignedPricelistsInputSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

const PricelistCsvDocSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
});

const UnassignedPricelistDocSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
  uploadedByName: z.string().nullable(),
  csvDocuments: z.array(PricelistCsvDocSchema),
});

export const ListUnassignedPricelistsResponseSchema = z.object({
  documents: z.array(UnassignedPricelistDocSchema),
  totalCount: z.number(),
});

export const ListAllPricelistsInputSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** Case-insensitive match against the uploaded file's name. */
  search: z.string().max(200).optional(),
});

const AllPricelistDocSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
  companyId: z.uuid().nullable(),
  companyName: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  csvDocuments: z.array(PricelistCsvDocSchema),
});

export const ListAllPricelistsResponseSchema = z.object({
  documents: z.array(AllPricelistDocSchema),
  totalCount: z.number(),
});

export const GetPricelistInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

// The AS/400 branch map (AS400_BRANCHES_BY_DIVISION / as400BranchesForDivision)
// lives with the rest of the AS/400 division and record-key helpers in
// products.contract.ts; the branch percentages below are read against it.

/**
 * One division's approximate price change. Divisions come from the org's
 * DataSources, so the set is dynamic; `divisionCode` snapshots the DataSource's
 * title at save time. Divisions that price per branch (GEN-2325) store one
 * entry per branch, distinguished by `branchCode`; an entry without a
 * `branchCode` is the division-wide value.
 */
export const DivisionPriceChangeSchema = z.object({
  dataSourceId: z.uuid(),
  divisionCode: z.string().min(1),
  branchCode: z.string().min(1).optional(),
  percent: PercentStringSchema,
});

/** The manually maintained pricelist fields. */
export const PricelistDetailsSchema = z.object({
  divisionPriceChanges: z.array(DivisionPriceChangeSchema),
  notes: z.string().nullable(),
  fromTariffChange: z.boolean(),
  anticipatedVendorChangePercent: PercentStringSchema.nullable(),
  /** When the details were last saved; null when never filled in. */
  updatedAt: z.date().nullable(),
});

export const GetPricelistResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  createdAt: z.date(),
  companyId: z.uuid().nullable(),
  companyName: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  csvDocuments: z.array(PricelistCsvDocSchema),
  details: PricelistDetailsSchema,
});

export const UpdatePricelistDetailsInputSchema = z.object({
  pdfDocumentId: z.uuid(),
  divisionPriceChanges: z.array(DivisionPriceChangeSchema),
  notes: z.string().nullable(),
  fromTariffChange: z.boolean(),
  anticipatedVendorChangePercent: PercentStringSchema.nullable(),
});

export const UpdatePricelistDetailsResponseSchema = z.object({
  details: PricelistDetailsSchema,
});

export const ListPricelistEventsInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const PricelistEventSchema = z.object({
  id: z.uuid(),
  action: z.nativeEnum(PricelistEventAction),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: z.uuid().nullable(),
  /** Display name of the acting user; null for system actions or deleted users. */
  actorName: z.string().nullable(),
  createdAt: z.date(),
});

export const ListPricelistEventsResponseSchema = z.object({
  events: z.array(PricelistEventSchema),
});

export const ListPricelistVersionsInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const PricelistVersionSchema = z.object({
  id: z.uuid(),
  /** 1-based, per pricelist. */
  versionNumber: z.number().int().positive(),
  tableCount: z.number().int().nonnegative(),
  rowCount: z.number().int().nonnegative(),
  source: z.nativeEnum(PricelistVersionSource),
  /** Whether this version is the pricelist's live contents. */
  isCurrent: z.boolean(),
  /**
   * True when the version's CSV text exceeded the inline-snapshot limit and
   * only counts were kept. Such a version cannot be restored.
   */
  contentOmitted: z.boolean(),
  /** Display name of the version's author; null for system or deleted users. */
  createdByName: z.string().nullable(),
  createdAt: z.date(),
});

export const ListPricelistVersionsResponseSchema = z.object({
  versions: z.array(PricelistVersionSchema),
});

export const RestorePricelistVersionInputSchema = z.object({
  pdfDocumentId: z.uuid(),
  versionId: z.uuid(),
});

export const RestorePricelistVersionResponseSchema = z.object({
  versionId: z.uuid(),
  versionNumber: z.number().int().positive(),
});

export const UpdateExtractedCsvInputSchema = z.object({
  csvDocumentId: z.uuid(),
  /** The full replacement contents — every table, in tab order. */
  tables: z.array(PricelistTableSchema).min(1),
});

export const UpdateExtractedCsvResponseSchema = z.object({
  csvDocumentId: z.uuid(),
});

export const RetryExtractionInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const RetryExtractionResponseSchema = z.object({
  csvDocumentId: z.uuid(),
});

export const AssignPricelistToCompanyInputSchema = z.object({
  pdfDocumentId: z.uuid(),
  companyId: z.uuid(),
});

export const AssignPricelistToCompanyResponseSchema = z.object({
  companyId: z.uuid(),
  companyName: z.string(),
});

export const DeletePricelistInputSchema = z.object({
  pdfDocumentId: z.uuid(),
});

export const DeletePricelistResponseSchema = z.object({
  success: z.boolean(),
});

export const pricelistContracts = {
  getUploadUrl: oc
    .input(GetPricelistUploadUrlInputSchema)
    .output(GetPricelistUploadUrlResponseSchema)
    .route({
      method: "POST",
      summary: "Reserve a presigned staging URL for a pricelist upload",
    }),
  uploadPricelist: oc
    .input(UploadPricelistInputSchema)
    .output(UploadPricelistResponseSchema)
    .route({
      method: "POST",
      summary: "Register an uploaded pricelist file and start its extraction",
    }),
  getExtractedCsv: oc
    .input(GetExtractedCsvInputSchema)
    .output(GetExtractedCsvResponseSchema)
    .route({
      method: "GET",
      summary:
        "Fetch a stored extracted CSV, whole or one row window at a time",
    }),
  searchExtractedCsv: oc
    .input(SearchExtractedCsvInputSchema)
    .output(eventIterator(PricelistSearchEventSchema))
    // POST, not GET: the query belongs in a body, and the response is a stream
    // rather than a cacheable document.
    .route({
      method: "POST",
      summary: "Search one pricelist's extracted tables, streaming matches",
    }),
  getDocumentUrl: oc
    .input(GetPricelistDocumentUrlInputSchema)
    .output(GetPricelistDocumentUrlResponseSchema)
    .route({
      method: "GET",
      summary: "Get a short-lived signed URL for a pricelist document",
    }),
  listUnassigned: oc
    .input(ListUnassignedPricelistsInputSchema)
    .output(ListUnassignedPricelistsResponseSchema)
    .route({
      method: "GET",
      summary: "List pricelist PDFs not assigned to any company",
    }),
  listAll: oc
    .input(ListAllPricelistsInputSchema)
    .output(ListAllPricelistsResponseSchema)
    .route({
      method: "GET",
      summary: "List all uploaded pricelist PDFs sorted by upload date",
    }),
  get: oc
    .input(GetPricelistInputSchema)
    .output(GetPricelistResponseSchema)
    .route({
      method: "GET",
      summary:
        "Get one pricelist source document with its company assignment, extracted CSV, and details",
    }),
  updateDetails: oc
    .input(UpdatePricelistDetailsInputSchema)
    .output(UpdatePricelistDetailsResponseSchema)
    .route({
      method: "PUT",
      summary:
        "Replace a pricelist's manually maintained details (division price changes, tariff flag, anticipated change, notes)",
    }),
  listEvents: oc
    .input(ListPricelistEventsInputSchema)
    .output(ListPricelistEventsResponseSchema)
    .route({
      method: "GET",
      summary: "List a pricelist's history events, newest first",
    }),
  listVersions: oc
    .input(ListPricelistVersionsInputSchema)
    .output(ListPricelistVersionsResponseSchema)
    .route({
      method: "GET",
      summary: "List a pricelist's saved versions, newest first",
    }),
  restoreVersion: oc
    .input(RestorePricelistVersionInputSchema)
    .output(RestorePricelistVersionResponseSchema)
    .route({
      method: "POST",
      summary:
        "Restore a pricelist's tables to a previous version and make it current",
    }),
  updateExtractedCsv: oc
    .input(UpdateExtractedCsvInputSchema)
    .output(UpdateExtractedCsvResponseSchema)
    .route({
      method: "POST",
      summary: "Replace a stored extracted CSV with edited table contents",
    }),
  retryExtraction: oc
    .input(RetryExtractionInputSchema)
    .output(RetryExtractionResponseSchema)
    .route({
      method: "POST",
      summary:
        "Re-extract CSV from a pricelist PDF, replacing or creating the CSV document",
    }),
  assignToCompany: oc
    .input(AssignPricelistToCompanyInputSchema)
    .output(AssignPricelistToCompanyResponseSchema)
    .route({
      method: "POST",
      summary:
        "Assign or re-assign a pricelist PDF to a company, replacing any existing company assignment",
    }),
  delete: oc
    .input(DeletePricelistInputSchema)
    .output(DeletePricelistResponseSchema)
    .route({
      method: "DELETE",
      summary:
        "Soft-delete a pricelist so it disappears from every pricelist view, keeping the record recoverable",
    }),
} as const;

export type PricelistTable = z.infer<typeof PricelistTableSchema>;
export type ExtractedCsvTable = z.infer<typeof ExtractedCsvTableSchema>;
export type GetExtractedCsvResponse = z.infer<
  typeof GetExtractedCsvResponseSchema
>;
export type GetPricelistUploadUrlInput = z.infer<
  typeof GetPricelistUploadUrlInputSchema
>;
export type GetPricelistUploadUrlResponse = z.infer<
  typeof GetPricelistUploadUrlResponseSchema
>;
export type UploadPricelistInput = z.infer<typeof UploadPricelistInputSchema>;
export type UploadPricelistResponse = z.infer<
  typeof UploadPricelistResponseSchema
>;
export type GetExtractedCsvInput = z.infer<typeof GetExtractedCsvInputSchema>;
export type SearchExtractedCsvInput = z.infer<
  typeof SearchExtractedCsvInputSchema
>;
export type PricelistSearchHit = z.infer<typeof PricelistSearchHitSchema>;
export type PricelistSearchEvent = z.infer<typeof PricelistSearchEventSchema>;
export type GetPricelistDocumentUrlInput = z.infer<
  typeof GetPricelistDocumentUrlInputSchema
>;
export type GetPricelistDocumentUrlResponse = z.infer<
  typeof GetPricelistDocumentUrlResponseSchema
>;
export type ListAllPricelistsResponse = z.infer<
  typeof ListAllPricelistsResponseSchema
>;
export type AllPricelistDoc = ListAllPricelistsResponse["documents"][number];
export type GetPricelistInput = z.infer<typeof GetPricelistInputSchema>;
export type GetPricelistResponse = z.infer<typeof GetPricelistResponseSchema>;
export type UpdateExtractedCsvInput = z.infer<
  typeof UpdateExtractedCsvInputSchema
>;
export type DivisionPriceChange = z.infer<typeof DivisionPriceChangeSchema>;
export type PricelistDetails = z.infer<typeof PricelistDetailsSchema>;
export type UpdatePricelistDetailsInput = z.infer<
  typeof UpdatePricelistDetailsInputSchema
>;
export type PricelistEventDTO = z.infer<typeof PricelistEventSchema>;
export type ListPricelistEventsResponse = z.infer<
  typeof ListPricelistEventsResponseSchema
>;
export type PricelistVersionDTO = z.infer<typeof PricelistVersionSchema>;
export type ListPricelistVersionsResponse = z.infer<
  typeof ListPricelistVersionsResponseSchema
>;
export type RestorePricelistVersionInput = z.infer<
  typeof RestorePricelistVersionInputSchema
>;
export type AssignPricelistToCompanyInput = z.infer<
  typeof AssignPricelistToCompanyInputSchema
>;
export type AssignPricelistToCompanyResponse = z.infer<
  typeof AssignPricelistToCompanyResponseSchema
>;
export type DeletePricelistInput = z.infer<typeof DeletePricelistInputSchema>;
export type DeletePricelistResponse = z.infer<
  typeof DeletePricelistResponseSchema
>;
